/*
  bf-training-actions.js
  Every interaction in the member app.

  These call the backend and then re-render from what it returned. None of them
  update the screen optimistically: if the server refused, the member needs to
  see the refusal, not a success that quietly disagrees with their record.
*/
(function () {
  'use strict';
  var A = window.BFTrainingApp;
  if (!A) return;
  var H = A.helpers;
  var api = H.api, S = H.state, CFG = H.config;

  var root = document.getElementById('bf-training-app');
  if (!root) return;

  /* ANSWERS CARRY WHO GAVE THEM AND WHEN.
     Hydration from the account needs two things this used to lose. `editedAt`
     says whether what is in this browser is newer than what is stored, so a
     deliberate edit is not overwritten by a reload. `owner` says whose answers
     these are, so one person's session cannot be applied to another's account
     on a shared device — the questions would otherwise prefill with a
     stranger's goal and equipment. */
  function setOnb(patch) {
    var KEY = 'bf_onboarding', v = {};
    try { v = JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (e) {}
    Object.keys(patch).forEach(function (k) { v[k] = patch[k]; });
    /* Only a real answer counts as an edit. Bookkeeping the app writes to
       itself (acceptance flags, pending states) must not make stale answers
       look newer than the account. */
    var ANSWERS = ['routine', 'goal', 'goals', 'comfort', 'equipment', 'days', 'length'];
    var touchedAnAnswer = Object.keys(patch).some(function (k) {
      return ANSWERS.indexOf(k) > -1;
    });
    if (touchedAnAnswer) {
      v.editedAt = Date.now();
      if (A.__onboardingOwner) v.owner = A.__onboardingOwner();
    }
    try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {}
  }

  /* THE ANSWER HAS TO BE SEEN, NOT JUST WRITTEN.
     Moving the refusal into the tapped row fixed it rendering ~1,100px down
     the page. It did not guarantee the member can READ it. The slot sits
     directly below the Save button, and on a phone the keyboard is up while
     you are looking at that button — Becca reported she could still see Save
     with the keyboard up, which means the strip immediately under it is what
     the keyboard covers. A message written into that strip is exactly as
     silent as one written at the bottom of the page, and silence after Save
     is the whole bug: it reads as a rule.

     visualViewport reports what the keyboard actually leaves visible, so this
     asks instead of guessing a keyboard height per device.

     It scrolls ONLY when the message would otherwise be off screen. A save
     you are already looking at must not move the page — that was its own
     reported bug ("it does jump me back to the top"), and re-introducing it
     to fix this one would be a poor trade. */
  function ensureSeen(el) {
    if (!el || !el.getBoundingClientRect) return;
    var vv = typeof window !== 'undefined' && window.visualViewport;
    var top = vv ? (vv.offsetTop || 0) : 0;
    var height = vv ? vv.height : (typeof window !== 'undefined' ? window.innerHeight : 0);
    if (!height) return;
    var r;
    try { r = el.getBoundingClientRect(); } catch (e) { return; }
    if (!r) return;
    if (r.top >= top && r.bottom <= top + height) return;   /* already readable */
    /* SCROLL BY A COMPUTED AMOUNT, NOT scrollIntoView.
       scrollIntoView({block:'center'}) centres within the LAYOUT viewport,
       which on iOS does not shrink when the keyboard opens — that separation
       is the whole reason visualViewport exists. Verified on the live page
       with the keyboard height stood in: scrollIntoView moved the message 42px
       and left 19px of it still underneath the keyboard. Centring it in the
       strip the keyboard actually leaves moved it 217px and cleared it. */
    var targetTop = top + Math.max(0, (height - (r.height || 0)) / 2);
    var delta = Math.round(r.top - targetTop);
    if (!delta) return;
    if (typeof window !== 'undefined' && window.scrollBy) { window.scrollBy(0, delta); return; }
    if (el.scrollIntoView) { try { el.scrollIntoView({ block: 'center' }); } catch (e) { el.scrollIntoView(); } }
  }
  function msg(sel, text) {
    var e = root.querySelector(sel);
    if (!e) return;
    e.textContent = text;
    /* An empty string clears the slot; there is nothing to scroll to. */
    if (text) ensureSeen(e);
  }
  function invalidate() { S.progress = null; S.credits = null; S.sms = null; S.consultations = null; }


  /* ---- rest timer -------------------------------------------------------
     Starts when a set is saved, because that is the moment rest begins. It is
     a display, never a gate: the member can log the next set, step to another
     exercise or leave at any point while it runs. Nothing waits on it, and it
     cannot fail in a way that blocks logging.

     No sound. A phone on silent in a gym is the normal case, so a countdown
     that depends on audio would be a countdown that usually does not work. */
  var restEnd = 0, restTick = null;
  var REST_KEY = 'bf_rest_until';

  function restRegion() { return root.querySelector('[data-rest-region]'); }

  /* SWAP THE STATES, NEVER THE REGION.
     The region used to carry `hidden` and sat below the rounds. It now sits
     ABOVE them, so hiding it would push every row down the screen the moment a
     rest started. The box stays; what changes is which half of it is showing.
     Safe to call with an older DOM that has neither child. */
  function showRest(running) {
    var el = restRegion();
    if (!el || !el.querySelector) return;
    var idle = el.querySelector('[data-rest-idle]');
    var live = el.querySelector('[data-rest-running]');
    if (idle) idle.hidden = !!running;
    if (live) live.hidden = !running;
    if (!idle && !live) el.hidden = !running;   // pre-split markup
  }

  /* REST IS WALL-CLOCK. It does not pause because the member looked at
     something. Going to read a setup guide, pick an approved alternative or
     glance at a suggestion and coming back must find the countdown where it
     actually is — so the END TIME is stored, not the remaining seconds, and
     the interval is simply re-attached when the workout screen comes back. */
  function saveRestUntil(ms) {
    try {
      if (ms) sessionStorage.setItem(REST_KEY, String(ms));
      else sessionStorage.removeItem(REST_KEY);
    } catch (e) {}
  }
  function savedRestUntil() {
    try { return Number(sessionStorage.getItem(REST_KEY)) || 0; } catch (e) { return 0; }
  }

  /** Re-attach the countdown if one is still running. Safe to call any time. */
  function resumeRest() {
    var until = savedRestUntil();
    if (!until || until <= Date.now()) { saveRestUntil(0); return false; }
    var el = restRegion();
    if (!el) return false;
    restEnd = until;
    showRest(true);
    paintRest();
    if (!restTick) restTick = setInterval(paintRest, 250);
    return true;
  }
  A.__resumeRest = resumeRest;

  function paintRest() {
    var el = restRegion();
    if (!el) { stopRest(); return; }
    var left = Math.max(0, Math.ceil((restEnd - Date.now()) / 1000));
    var clock = el.querySelector('[data-rest-clock]');
    var label = el.querySelector('[data-rest-label]');
    if (clock) {
      clock.textContent = Math.floor(left / 60) + ':' + ('0' + (left % 60)).slice(-2);
    }
    if (left <= 0) {
      if (label) label.textContent = 'Rest done — next set when you are ready.';
      stopRest();
      saveRestUntil(0);
      /* Back to the start control, so the next rest is one tap away and the
         box does not sit there showing a finished countdown. */
      showRest(false);
      return;
    }
    if (label) label.textContent = 'Resting';
  }

  function stopRest() {
    if (restTick) { clearInterval(restTick); restTick = null; }
  }

  function startRest(seconds) {
    var el = restRegion();
    if (!el || !seconds) return;
    restEnd = Date.now() + seconds * 1000;
    saveRestUntil(restEnd);
    showRest(true);
    stopRest();
    paintRest();
    restTick = setInterval(paintRest, 250);
  }

  /** "90s", "1m 30s", 75 -> seconds. Unparseable rest simply starts no timer. */
  function restSeconds(rest) {
    if (rest == null) return 0;
    if (typeof rest === 'number') return rest > 0 ? rest : 0;
    var text = String(rest);
    var m = /(\d+)\s*m/.exec(text);
    var sec = /(\d+)\s*s/.exec(text);
    var total = (m ? Number(m[1]) * 60 : 0) + (sec ? Number(sec[1]) : 0);
    if (!total) { var bare = /(\d+)/.exec(text); total = bare ? Number(bare[1]) : 0; }
    return total;
  }
  A.__restSeconds = restSeconds;

  /* A route change detaches the interval, because it must not outlive the
     screen that owns it — but the stored END TIME survives, so coming back to
     the workout re-attaches the same countdown rather than starting a new one
     or pretending rest was over. */
  window.addEventListener('hashchange', function () {
    stopRest();
    if ((location.hash || '').indexOf('workout') > -1) {
      // After the screen has rendered.
      setTimeout(resumeRest, 60);
      setTimeout(resumeRest, 400);
    }
  });
  setTimeout(resumeRest, 800);

  /* EVERY ACTION REPORTS, INCLUDING THE ONES THAT THROW.
     This was one `async function` serving every control in the app, with no
     guard of its own: fourteen awaits sat directly in its body outside any
     try. Each branch handles the failures api() REPORTS —

         if (!f.ok) { msg('[data-finish-msg]', 'Could not report that…'); return; }

     — and none that it THROWS. api() could throw until the refresh fix
     landed, so this was not hypothetical, and leaving it would mean the next
     one is silent too.

     A rejection here is worse than a blank screen: nothing changes at all.
     The member presses Complete workout, the button does not respond, there
     is no error and no spinner, and the screen that would have explained is
     never drawn. That is a dead button, and it is indistinguishable from one.

     The body is lifted into a named function rather than wrapped in place, so
     the guard does not re-indent seven hundred lines of working code. */
  root.addEventListener('click', function (e) {
    handleClick(e).catch(actionThrew);
  });

  /* What an unexpected throw is allowed to say.
     Deliberately NOT "nothing was saved": the throw can happen before or
     after the server acted, so that is a guess, and a wrong one half the
     time. It names the one safe next step instead. */
  function actionThrew() {
    var SLOTS = ['[data-log-msg]', '[data-finish-msg]', '[data-schedule-msg]',
                 '[data-onb-msg]', '[data-sms-msg]', '[data-reflect-msg]',
                 '[data-chat-msg]', '[data-consult-msg]', '[data-shared-msg]',
                 '[data-cart-msg]'];
    for (var i = 0; i < SLOTS.length; i++) {
      if (root.querySelector(SLOTS[i])) {
        msg(SLOTS[i], 'Something went wrong and we could not confirm that. ' +
                      'Reload the page to see where you are before trying again.');
        return;
      }
    }
  }

  async function handleClick(e) {
    /* ---- onboarding ---------------------------------------------------- */
    var choice = e.target.closest('[data-onb-key]');
    if (choice) {
      /* Selection NEVER auto-advances. The member confirms with Continue, so a
         mis-tap is not a decision. Changing an answer withdraws any schedule
         they had accepted, because the proposal it belonged to is gone. */
      var k = choice.getAttribute('data-onb-key');
      var v = choice.getAttribute('data-onb-value');
      var st = A.__onboardingState();
      var patch = {};
      if (k === 'goals') {
        /* SEVERAL THINGS CAN MATTER AT ONCE, AND TAPPING ONE MUST NOT CLEAR
           THE REST. Someone can want to build muscle, get stronger and lose
           body fat; making them pick one threw away what they told us and
           then planned against the remainder. Tapping a selected card
           deselects just that card. */
        var cur = (st.goals || []).slice();
        var at = cur.indexOf(v);
        if (at > -1) cur.splice(at, 1); else cur.push(v);
        patch.goals = cur;
      } else {
        patch[k] = v;
      }
      patch.accepted = false;
      setOnb(patch);
      H.render();
      // Focus stays on the control the member just chose, after the re-render.
      setTimeout(function () {
        var el = root.querySelector('[data-onb-key="' + k + '"][data-onb-value="' + CSS.escape(v) + '"]');
        if (el) el.focus({ preventScroll: true });
      }, 0);
      return;
    }

    var day = e.target.closest('[data-onb-day]');
    if (day) {
      var d = day.getAttribute('data-onb-day');
      var cur = (A.__onboardingState().days || []).slice();
      var at = cur.indexOf(d);
      if (at > -1) cur.splice(at, 1); else cur.push(d);
      var ORDER = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
      cur.sort(function (a, b) { return ORDER.indexOf(a) - ORDER.indexOf(b); });
      setOnb({ days: cur, accepted: false });
      H.render();
      setTimeout(function () {
        var el = root.querySelector('[data-onb-day="' + d + '"]');
        if (el) el.focus({ preventScroll: true });
      }, 0);
      return;
    }

    var nav = e.target.closest('[data-onb-next], [data-onb-back]');
    if (nav) {
      /* THE QUESTION ORDER LIVES IN ONE PLACE, AND IT IS NOT THIS ONE.
         Onboarding publishes it as __onboardingSteps. This used to hold a
         third hard-coded copy — alongside STEPS and the defs array — and when
         the questionnaire was reordered on 2026-10-10 Continue skipped a
         question while the route and the progress counter were both correct,
         because only two of the three copies had been changed.

         The literal below is a fallback for onboarding.js not being loaded,
         not a second source of truth. */
      var steps = (A.__onboardingSteps && A.__onboardingSteps.length)
        ? Array.prototype.slice.call(A.__onboardingSteps)
        : ['routine', 'goal', 'equipment', 'comfort', 'days', 'time'];
      var ROUTES = steps.concat(['plan', 'save']);
      var here = ROUTES.indexOf((location.hash || '').replace(/^#/, '').split('/')[0]);
      if (here < 0) here = 0;
      var to = nav.hasAttribute('data-onb-back') ? here - 1 : here + 1;
      location.hash = '#' + ROUTES[Math.max(0, Math.min(ROUTES.length - 1, to))];
      return;
    }

    var accept = e.target.closest('[data-onb-accept]');
    if (accept) {
      /* ACCEPTING IS THE MEMBER'S ACT, AND WHO CONFIRMS IT DEPENDS ON WHO THEY
         ARE.

         Signed out there is no server to ask, so the local answer IS the
         answer and is carried through sign-in.

         Signed in, only the ACCOUNT can confirm it. The button must never read
         "chosen" because a request was merely sent — a save that fails and a
         save that succeeded look identical to someone who has already walked
         away. So the states are distinct: saving, saved, failed. Nothing
         claims saved until the account says so, and a failure says so plainly
         and leaves the button offering to try again. */
      var days = accept.getAttribute('data-onb-accept').split(',').filter(Boolean);

      if (!H.signedIn()) {
        setOnb({ accepted: true, acceptedDays: days, acceptPending: false, acceptFailed: false });
        H.render();
        return;
      }

      setOnb({ acceptedDays: days, acceptPending: true, acceptFailed: false, accepted: false });
      H.render();
      var ar = await A.__persistPreferences({ acceptSchedule: days });
      var ok = !!(ar && ar.ok);
      setOnb({ acceptPending: false, acceptFailed: !ok, accepted: ok });
      H.render();
      msg('[data-onb-msg]', ok
        ? 'Saved to your account. Your week is ' + days.join(', ') + '.'
        : 'We could not save that. Your answers are safe here \u2014 try again.');
      return;
    }

    if (e.target.closest('[data-onb-signin]')) {
      /* This IS the press. Recorded before either branch, so it covers the
         member who is already signed in and the one who signs in first and
         comes back to '#save'. */
      if (window.__bfMarkSaveIntent) window.__bfMarkSaveIntent();
      /* The existing Shopify account flow. No duplicate email or code form is
         built here, and the answers survive the trip because they are held
         locally until the first authenticated save. */
      /* ALREADY SIGNED IN MEANS SAVE, NOT SIGN IN AGAIN. This called signIn()
         unconditionally, so a member who was already authenticated took a
         pointless round trip through Shopify and came back without having
         saved anything — measured on the live site, where it landed them on
         the training landing page. */
      if (H.signedIn()) { location.hash = '#save'; return; }

      var auth = H.state.auth || (window.BFTrainingAuth && window.BF_TRAINING_AUTH_CONFIG
        ? new window.BFTrainingAuth(window.BF_TRAINING_AUTH_CONFIG) : null);
      /* RETURN TO THE SAVE, NOT THE REVEAL. Coming back to '#plan' showed the
         member the screen they had just pressed the button on and asked them
         to press it again. '#save' is where the write happens.

         And a FULL PATH, not a bare hash: the callback finishes by replacing
         the location with this value, and '#save' alone only sets the hash on
         the callback page, which left a signed-in member stranded there. */
      if (auth && auth.signIn) { auth.signIn(location.pathname + '#save'); return; }
      msg('[data-onb-msg]', 'Sign in is unavailable right now.');
      return;
    }

    /* ---- rest timer controls ------------------------------------------ */
    /* START THE REST YOURSELF.
       Until now only a successful Save started a countdown, so a member who
       rested between sets without logging them had no timer — skipping the
       log quietly cost them a feature, which is the same defect as the screen
       implying logging was required. Asked for as "an optional timer to click
       to record your rest time". It writes nothing and calls nothing: it is a
       stopwatch, not a record. */
    var restStart = e.target.closest('[data-rest-start]');
    if (restStart) {
      startRest(restSeconds(restStart.getAttribute('data-rest-start')));
      return;
    }

    if (e.target.closest('[data-rest-skip]')) {
      stopRest();
      saveRestUntil(0);          // skipped is over, not merely off-screen
      showRest(false);
      return;
    }
    var addRest = e.target.closest('[data-rest-add]');
    if (addRest) {
      var extra = Number(addRest.getAttribute('data-rest-add')) || 30;
      restEnd = Math.max(restEnd, Date.now()) + extra * 1000;
      saveRestUntil(restEnd);
      if (!restTick) restTick = setInterval(paintRest, 250);
      paintRest();
      return;
    }

    /* ---- accept the proposed schedule --------------------------------- */
    var accept = e.target.closest('[data-accept-schedule]');
    if (accept) {
      /* THE MEMBER accepts it. The proposal was shown with its reason and the
         days came from their own availability; nothing was stored until this
         click. Their answer is never rewritten behind them to manufacture a
         match. */
      var days = accept.getAttribute('data-accept-schedule').split(',').filter(Boolean);
      msg('[data-schedule-msg]', 'Saving…');
      var sres = await api('/preferences', { method: 'POST', body: { trainingDays: days } });
      msg('[data-schedule-msg]', sres.ok
        ? 'Saved. Your week is ' + days.join(', ') + '.'
        : 'Could not save that. Your days are unchanged.');
      if (sres.ok) { S.preferences = null; S.program = null; S.profile = null; invalidate(); H.render(); }
      return;
    }

    /* ---- move between exercises --------------------------------------- */
    var step = e.target.closest('[data-station-step]');
    if (step) {
      /* An explicit choice, so it is SAVED against this workout rather than
         held for the life of the screen: going to read the setup guide or pick
         an approved alternative and coming back must land on the same
         exercise. Without this, checking a number bounced the member forward
         to the first unfinished set again. */
      var occ = step.getAttribute('data-occurrence');
      if (A.__savePosition) A.__savePosition(occ, Number(step.getAttribute('data-station-step')));
      S.sets = null;
      H.render();
      return;
    }

    /* ---- log a set ---------------------------------------------------- */
    var pick = e.target.closest('[data-pick-variant]');
    if (pick) {
      /* Stored per occurrence and per station, so it survives navigation and a
         reload and is still the choice when the member comes back to it. */
      try {
        sessionStorage.setItem('bf_variant:' + (pick.getAttribute('data-occurrence') || 'none') +
          ':' + pick.getAttribute('data-station'), pick.getAttribute('data-pick-variant'));
      } catch (e2) {}
      H.render();
      return;
    }

    var log = e.target.closest('[data-log-set]');
    if (log) {
      var idx = Number(log.getAttribute('data-log-set'));
      var station = log.getAttribute('data-station');
      /* FIND THE BOXES IN THIS ROW, NOT THE FIRST ONES ON THE SCREEN.
         These looked up `[data-log-reps="1"]` from the root, so they depended
         on only ever rendering one exercise at a time — the set index was
         unique by accident, not by design. Put a circuit's two exercises on
         one screen and saving the push-up's first set reads the squat's box.
         snapshot() already scopes by closest('.bf-row'), so the two halves of the
         same feature disagreed about what identifies a row. */
      var scope = (log.closest && log.closest('.bf-row')) || root;
      /* Say it IN THIS ROW when the row has somewhere to say it, and fall back
         to the panel for anything that renders without one. */
      var rowMsgEl = scope !== root && scope.querySelector
        ? scope.querySelector('[data-log-msg-row]') : null;
      var say = function (text) {
        if (rowMsgEl) {
          rowMsgEl.textContent = text;
          if (text) ensureSeen(rowMsgEl);
          return;
        }
        msg('[data-log-msg]', text);
      };
      var w = scope.querySelector('[data-log-weight="' + idx + '"]') ||
              root.querySelector('[data-log-weight="' + idx + '"]');
      var r = scope.querySelector('[data-log-reps="' + idx + '"]') ||
              root.querySelector('[data-log-reps="' + idx + '"]');
      /* A hold is measured in seconds and a dead bug in reps per side. Sending
         a plank's seconds in the reps column would record "45 reps". */
      var unit = r ? r.getAttribute('data-unit') : 'reps';
      var val = r && r.value ? Number(r.value) : null;

      /* AN EMPTY BOX IS NOT A SET.
         Pressing Save with nothing typed used to post reps: null, the backend
         accepted it, and the row came back reading "Logged" — a set recorded
         with no number in it. Reported from a phone: "It said logged on the
         first even though the number was empty."

         It is not cosmetic. sessionProgress counts a row's existence, not its
         contents, so an empty save marks that set done, advances the circuit,
         and carries the session towards a completion nobody performed.

         The box keeps focus so the fix is one tap: type the number and press
         Save again. */
      if (val == null || isNaN(val)) {
        /* NAMING THE ALTERNATIVE, because the refusal used to be the whole
           answer. "Add your reps first." from the loudest button on the
           screen is indistinguishable from a requirement, and a tester read
           it as one: "logging progress shouldn't be required to complete
           workout and go to next one." It never was. The guard stays; what
           it says now is that the box is how you record the set, not how you
           are allowed to move on. */
        say('Add your ' + (unit === 'seconds' ? 'seconds' : 'reps') +
          ' to log this set. Logging is optional \u2014 the session completes either way.');
        if (r && typeof r.focus === 'function') { try { r.focus({ preventScroll: true }); } catch (e) { r.focus(); } }
        return;
      }

      say('Saving…');
      var res = await api('/workouts', { method: 'POST', body: {
        plannedSessionId: CFG.sessionKey || 'starter-1',
        stationId: station, setIndex: idx,
        weightMilli: w && w.value ? Math.round(Number(w.value) * 1000) : null,
        weightUnit: 'lb',
        reps: unit === 'seconds' ? null : val,
        seconds: unit === 'seconds' ? val : null,
        variant: log.getAttribute('data-variant') || null
      } });
      /* A FAILED SAVE LEAVES THE ENTRY ALONE. The inputs are not cleared and
         the row is not marked, so the Save button beside the numbers they
         already typed is the retry. */
      /* NAME THE VALUE, NOT JUST THE EVENT. "Set 2 saved." leaves the member
         to trust that the right number went in — and the number is the whole
         point of pressing Save. Saying "Set 2 saved: 30 seconds." is the only
         confirmation that can be checked against what they typed, and it is
         what a screen reader announces through the live region. */
      /* "Saved" and "updated" are different events to the person who pressed
         the button: one records a set, the other changes a number they had
         already recorded. The write is the same upsert either way. */
      var correcting = log.getAttribute('data-correcting') === '1';
      var confirmation = res.ok
        ? 'Set ' + idx + (correcting ? ' updated' : ' saved') +
          (val ? ': ' + val + (unit === 'seconds' ? ' seconds' : ' reps') : '') + '.'
        : 'Could not save that set. Your numbers are still here \u2014 press Save to try again.';
      /* WRITTEN WHERE IT WILL SURVIVE.
         A failure does not redraw the screen, so the message can go up now and
         stay. A success DOES redraw, and this is where that message used to be
         written — about fifty lines before a re-render that replaces the whole
         screen and took it with it. It was destroyed every single time.
         Confirmed on an iPhone 17 Pro, Safari: "It doesn't say anything." The
         row text was the only feedback a member ever got, and after a save it
         is below the fold. A live region must also CHANGE while it is in the
         document to be announced; writing into markup that is about to be
         discarded announces nothing to anyone. The success path now writes it
         after the redraw, in announceSaved(). */
      if (!res.ok) say(confirmation);

      /* Show the set as logged straight away.
       *
       * The row was rendered from the log as it stood when the screen opened,
       * so without this a set you just saved still reads "Not logged" until you
       * navigate away and back — the data was in the database the whole time,
       * which makes it a worse bug, not a smaller one: it invites the member to
       * save the same set twice.
       *
       * Updated in place rather than re-rendering the screen, because a
       * re-render would wipe the weight and reps the member just typed, and
       * keeping those visible after logging is explicit in the design package.
       *
       * `sets` is dropped from the cache too, so the next screen that reads the
       * log gets it from the backend rather than from this patched-up copy. */
      if (res.ok) {
        /* Rest starts now, from this exercise's own prescription. */
        startRest(restSeconds(log.getAttribute('data-rest')));
        /* SAY THE SAME WORD THE RENDER SAYS.
           This wrote "Last: 10 reps", which is what a hint from a PREVIOUS
           workout looks like, while a reload of the same row says "Logged:
           10 reps". One state, two words, and the one shown immediately after
           saving was the one that reads like it has not been saved. Members
           could not tell which sets they had done without reloading, and
           re-saved sets they had already logged. */
        markRowLogged(log, val, unit);
        S.sets = null;
        /* THE "WHAT'S NEXT" CONTROL HAS TO MOVE TOO.
           Patching the row and the counters in place left the circuit step
           still saying "Round 1 of Box squat is next" after round 1 had been
           logged — the one control whose entire job is to say what to do now.
           Re-rendering is what recomputes it, and the typed numbers are put
           back afterwards so the member still sees what they just entered,
           which is why the in-place patch existed in the first place. */
        /* A SAVE IS NOT A NAVIGATION.
           The re-render is what recomputes the counters and the circuit's next
           step, so it stays — but it must leave the member exactly where they
           were. Reported from a phone, mid-session: "once I logged a rep, it
           took me to the top of the screen, so I had to scroll back down to log
           the second set." Every set, all session.

           Everything the member had typed is carried across, not just the row
           being saved: a second set entered ahead of time is their work too.
           Focus goes back to whatever held it, so the keyboard does not shut
           and reopen under them. */
        var keep = snapshot(root);
        await H.render({ keepPosition: true });
        restore(root, keep);
        announceSaved(root, confirmation);
      }
      return;
    }


  /* The confirmation, put up once the screen it lives on exists again.
     Separate function so the order is visible and so a test can hold it: the
     whole defect was that this ran before the redraw rather than after it. */
  function announceSaved(root, text) {
    var e = root.querySelector('[data-log-msg]');
    if (!e) return;
    /* Cleared first so the live region sees a CHANGE even when the same set is
       saved twice with the same value, which would otherwise be silent. */
    e.textContent = '';
    e.textContent = text;
  }

  /* WHAT THE MEMBER HAD BEFORE THE SCREEN WAS REDRAWN.
     Keyed by the attributes the markup already carries, so nothing has to be
     added to the DOM for this to work. `null` for the scroll means we never
     read one, which is different from having read zero. */
  function snapshot(root) {
    var vals = [];
    [].slice.call(root.querySelectorAll('[data-log-reps], [data-log-weight]'))
      .forEach(function (el) {
        if (!el.value) return;
        var row = el.closest('.bf-row');
        var btn = row && row.querySelector('[data-log-set]');
        if (!btn) return;
        vals.push({ set: btn.getAttribute('data-log-set'),
                    station: btn.getAttribute('data-station'),
                    which: el.hasAttribute('data-log-reps') ? 'reps' : 'weight',
                    value: el.value });
      });
    var active = document.activeElement;
    var focus = null;
    if (active && root.contains(active)) {
      var frow = active.closest && active.closest('.bf-row');
      var fbtn = frow && frow.querySelector('[data-log-set]');
      if (fbtn) {
        focus = { set: fbtn.getAttribute('data-log-set'),
                  station: fbtn.getAttribute('data-station'),
                  which: active.hasAttribute('data-log-reps') ? 'reps'
                       : active.hasAttribute('data-log-weight') ? 'weight' : null };
      }
    }
    return { vals: vals, focus: focus,
             scroll: typeof window.pageYOffset === 'number' ? window.pageYOffset : null };
  }

  function fieldIn(root, ref) {
    var btn = root.querySelector('[data-log-set="' + ref.set + '"]' +
                                 '[data-station="' + ref.station + '"]');
    var row = btn && btn.closest('.bf-row');
    if (!row) return null;
    return row.querySelector(ref.which === 'weight' ? '[data-log-weight]' : '[data-log-reps]');
  }

  function restore(root, keep) {
    keep.vals.forEach(function (v) {
      var el = fieldIn(root, v);
      /* EVERY NUMBER THE MEMBER TYPED, INCLUDING THE ONE THEY JUST SAVED.
         A saved set keeps its number in the box beside "Logged: 15 reps". That
         is deliberate, and it is the same thing markRowLogged above is careful
         not to disturb when it patches the row in place.

         It is what makes the button next to it honest. Edit is not a mode: it
         is this same save path with `data-correcting="1"`, so it submits
         whatever is in the box, and the empty-box guard refuses an empty one.
         Clear the field and Edit becomes a control that can only ever answer
         "Add your reps to log this set." until the member retypes a number
         they have already entered once. The number stays so that Edit means
         what it says.

         This comment used to claim the opposite — that a set which came back
         logged "has had its answer" and must not be written back — while the
         line below did what it still does. The code was right and the comment
         was wrong, which is worse than it sounds: on 2026-10-28 a tester's
         screenshot showing four logged rows each still holding the 15 she had
         typed was read as a defect on the strength of this comment alone, and
         very nearly "fixed".

         `!el.value` is the real guard, and it is about timing rather than
         logging: a member who starts typing the next set while this one is
         still saving must not have those keystrokes overwritten by a snapshot
         taken before they pressed a key. */
      if (el && !el.value) el.value = v.value;
    });
    if (keep.focus && keep.focus.which) {
      var f = fieldIn(root, keep.focus);
      if (f && typeof f.focus === 'function') { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
    }
    /* Last, because focusing can scroll on its own. */
    if (keep.scroll != null && typeof window.scrollTo === 'function') {
      try { window.scrollTo({ top: keep.scroll, behavior: 'auto' }); } catch (e) { window.scrollTo(0, keep.scroll); }
    }
  }

  /* Reflect a saved set across everything that counts it, without a re-render.
     A re-render would wipe the numbers the member just typed, which is why the
     original patched one line in place; the mistake was patching only that one
     line, so the exercise and session totals stayed stale until a reload. */
  function markRowLogged(btn, value, unit) {
    var root = document.getElementById('bf-training-app');
    if (!root) return;
    var row = btn.closest('.bf-row');
    if (row) {
      var status = row.querySelector('p');
      if (status) status.textContent = 'Logged' +
        (value ? ': ' + value + (unit === 'seconds' ? ' seconds' : ' reps') : '');
      row.classList.remove('bf-row-next');
      row.setAttribute('data-logged', '1');
    }
    /* The next unlogged set in this exercise becomes the one to do. */
    var rows = [].slice.call(root.querySelectorAll('.bf-row')).filter(function (x) {
      return x.querySelector('[data-log-set]');
    });
    var marked = false;
    rows.forEach(function (x) {
      x.classList.remove('bf-row-next');
      var p = x.querySelector('p');
      if (!marked && x.getAttribute('data-logged') !== '1' && p && !/^Logged/.test(p.textContent)) {
        x.classList.add('bf-row-next'); p.textContent = 'Next up'; marked = true;
      } else if (p && !/^Logged/.test(p.textContent)) {
        p.textContent = 'Not logged';
      }
    });
    /* Exercise and session progress, counted from what is now on screen. */
    var doneHere = rows.filter(function (x) {
      var p = x.querySelector('p'); return p && /^Logged/.test(p.textContent);
    }).length;
    var stationId = btn.getAttribute('data-station');
    var sess = root.querySelector('[data-session-row="' + stationId + '"]');
    if (sess) {
      var c = sess.querySelector('[data-session-count]');
      if (c) c.textContent = doneHere + ' of ' + rows.length + ' sets';
      var tagEl = sess.querySelector('[data-session-state]');
      if (tagEl && doneHere >= rows.length) tagEl.textContent = 'Done';
    }
  }

    /* ---- finish the session -------------------------------------------- */
    if (e.target.closest('[data-finish]')) {
      msg('[data-finish-msg]', 'Reporting…');
      /* The occurrence the button was rendered for, not the template key. A
         report retried after the next session has started must still land on
         the workout it was sent for. Falls back to the template key when the
         screen could not name one, which is what older clients send. */
      var fin = e.target.closest('[data-finish]');
      var finOcc = fin && fin.getAttribute('data-occurrence');
      var f = await api('/completions', { method: 'POST', body: {
        plannedSessionId: finOcc || CFG.sessionKey || 'starter-1', source: 'app' } });
      if (!f.ok) {
        /* A page left open from an earlier session cannot say which workout it
           means, and the server refuses to guess rather than finish the wrong
           one. Say what to do about it instead of a generic failure. */
        msg('[data-finish-msg]', (f.data && f.data.error === 'stale_client')
          ? (f.data.message || 'This page is out of date. Reload it and finish again.')
          : 'Could not report that right now.');
        return;
      }
      var d = f.data;
      msg('[data-finish-msg]',
        d.outcome === 'awarded' ? 'Workout reported. +' + d.creditsAwarded + ' credits.'
        : d.outcome === 'already_reported' ? 'Already reported. Nothing double counted.'
        : d.outcome === 'daily_cap' ? 'Reported. You have already earned today, so no extra credits.'
        : d.outcome === 'not_entitled' ? 'Workout recorded. Earning credits needs an active membership.'
        : 'Reported.');
      invalidate();
      setTimeout(function () { location.hash = '#complete'; }, 900);
      return;
    }

    /* ---- weekly reflection ---------------------------------------------- */
    if (e.target.closest('[data-reflect]')) {
      msg('[data-reflect-msg]', 'Saving…');
      var rf = await api('/reflections', { method: 'POST', body: {} });
      if (!rf.ok) { msg('[data-reflect-msg]', 'Not saved.'); return; }
      msg('[data-reflect-msg]', rf.data.awarded
        ? 'Reflection recorded. +' + rf.data.creditsAwarded + ' credits.'
        : rf.data.hasReflection ? 'Already recorded for this week.'
        : 'Not recorded — this needs an active membership.');
      invalidate();
      return;
    }

    /* +5 FOR SHARING. Claimed, not verified, and the screen says so. */
    if (e.target.closest('[data-shared]')) {
      msg('[data-shared-msg]', 'Saving\u2026');
      var sp = await api('/social-posts', { method: 'POST', body: {} });
      if (!sp.ok) { msg('[data-shared-msg]', 'Not saved.'); return; }
      msg('[data-shared-msg]', sp.data.awarded
        ? 'Thanks \u2014 +' + sp.data.creditsAwarded + ' credits.'
        : sp.data.hasSharedPost ? 'Already claimed this week.'
        : !sp.data.started ? 'This starts once your first week is running.'
        : 'Not recorded \u2014 this needs an active membership.');
      invalidate();
      return;
    }

    /* ---- sms ------------------------------------------------------------ */
    if (e.target.closest('[data-sms-submit]')) {
      var agree = root.querySelector('[data-sms-agree]');
      var phone = root.querySelector('[data-sms-phone]');
      if (!agree || !agree.checked) { msg('[data-sms-msg]', 'Tick the box to agree before we can text you.'); return; }
      msg('[data-sms-msg]', 'Saving…');
      var c = await api('/sms/consent', { method: 'POST', body: {
        agree: true, phone: phone ? phone.value : '', disclosureVersion: 'v1' } });
      if (!c.ok) {
        msg('[data-sms-msg]', (c.data && c.data.error === 'invalid_phone')
          ? "That doesn't look like a mobile number." : 'Could not save that right now.');
        return;
      }
      msg('[data-sms-msg]', c.data.verificationSent ? 'Code sent. Enter it below.'
        : 'Saved. No code was sent because texting is not switched on yet.');
      S.sms = null; H.render();
      return;
    }
    if (e.target.closest('[data-sms-confirm]')) {
      var code = root.querySelector('[data-sms-code]');
      msg('[data-sms-msg]', 'Checking…');
      var v = await api('/sms/verify', { method: 'POST', body: { code: code ? code.value : '' } });
      msg('[data-sms-msg]', v.ok ? 'Number confirmed.'
        : (v.data && v.data.error === 'incorrect_code') ? 'That code did not match.'
        : (v.data && v.data.error === 'code_expired') ? 'That code has expired.'
        : 'Could not confirm that right now.');
      if (v.ok) { S.sms = null; H.render(); }
      return;
    }
    if (e.target.closest('[data-sms-save]')) {
      var t = root.querySelector('[data-sms-time]');
      msg('[data-sms-msg]', 'Saving…');
      var p = await api('/sms/preferences', { method: 'POST', body: {
        trainingDays: [1, 3, 5], sendLocalTime: t ? t.value : '18:00' } });
      msg('[data-sms-msg]', p.ok ? 'Saved.' : 'Pick a valid time.');
      return;
    }
    if (e.target.closest('[data-sms-stop]')) {
      msg('[data-sms-msg]', 'Stopping…');
      await api('/sms/opt-out', { method: 'POST', body: {} });
      msg('[data-sms-msg]', 'Stopped. You will not get training texts.');
      S.sms = null; H.render();
      return;
    }

    /* ---- chat ----------------------------------------------------------- */
    var send = e.target.closest('[data-chat-send]');
    if (send) {
      var guide = send.getAttribute('data-guide');
      var input = root.querySelector('[data-chat-input]');
      var logEl = root.querySelector('[data-chat-log]');
      var text = input ? input.value.trim() : '';
      if (!text) { msg('[data-chat-msg]', 'Ask something first.'); return; }
      if (logEl) {
        logEl.insertAdjacentHTML('beforeend',
          '<div class="bf-panel" style="background:var(--bf-bg)"><p style="margin:0">' + H.esc(text) + '</p></div>');
      }
      if (input) input.value = '';
      msg('[data-chat-msg]', 'Thinking…');
      var ch = await api('/chat', { method: 'POST', body: { guide: guide, message: text } });
      var reply = (ch.data && ch.data.reply) || 'No answer available.';
      if (logEl) {
        logEl.insertAdjacentHTML('beforeend',
          '<div class="bf-panel"><div class="bf-eyebrow">' +
          (guide === 'todd' ? 'Todd AI' : 'Sophie AI') + ' · automated</div>' +
          '<p style="margin:8px 0 0">' + H.esc(reply) + '</p></div>');
        logEl.lastElementChild.scrollIntoView({ block: 'nearest' });
      }
      msg('[data-chat-msg]', (ch.data && ch.data.available) ? '' : 'This guide is not switched on yet.');
      return;
    }

    /* ---- consultations --------------------------------------------------- */
    var book = e.target.closest('[data-consult-book]');
    if (book) {
      var when = root.querySelector('[data-consult-when]');
      if (!when || !when.value) { msg('[data-consult-msg]', 'Choose a time first.'); return; }
      msg('[data-consult-msg]', 'Requesting…');
      var bk = await api('/consultations/book', { method: 'POST', body: {
        guide: (CFG.consultGuide || 'sophie'),
        startsAt: new Date(when.value).toISOString(),
        useFreeIntro: !book.getAttribute('data-paid') } });
      if (bk.ok) {
        msg('[data-consult-msg]', bk.data.state === 'confirmed'
          ? 'Confirmed by the booking provider.'
          : 'Requested. Not confirmed until the provider accepts it.');
      } else {
        var why = bk.data && bk.data.error;
        msg('[data-consult-msg]',
          why === 'slot_taken' ? 'That time has just gone. Pick another.'
          : why === 'free_intro_used' ? 'Your free introductory call has already been used.'
          : why === 'price_unavailable' ? 'A follow-up price has not been published yet.'
          : 'Booking is not available yet.');
      }
      S.consultations = null;
      return;
    }

    /* ---- supplement suggestions on or off --------------------------------- */
    var supp = e.target.closest('[data-supplements]');
    if (supp) {
      /* A STANDING ANSWER, not a dismissed card. Saved against the account so
         a member who said no is not asked again by a screen that forgot. */
      var want = supp.getAttribute('data-supplements');
      msg('[data-cart-msg]', 'Saving…');
      var sres = await api('/preferences', { method: 'POST', body: { supplements: want } });
      msg('[data-cart-msg]', sres.ok
        ? (want === 'no' ? 'Supplement suggestions are off.' : 'Supplement suggestions are back on.')
        : 'Could not save that.');
      if (sres.ok) { S.preferences = null; H.render(); }
      return;
    }

    /* ---- add to cart ------------------------------------------------------ */
    var add = e.target.closest('[data-add-handle], [data-add-variant]');
    if (add) {
      var id;
      var handle = add.getAttribute('data-add-handle');
      if (handle) {
        var picker = root.querySelector('[data-variant-for="' + handle.replace(/"/g, '') + '"]');
        id = picker ? Number(picker.value) : NaN;
      } else {
        id = Number(add.getAttribute('data-add-variant'));
      }
      if (!id || isNaN(id)) { msg('[data-cart-msg]', 'Choose an option first.'); return; }

      msg('[data-cart-msg]', 'Adding…');
      try {
        /* What the cart held BEFORE, so the result can be checked rather than
           assumed. /cart/add.js adds; it does not replace. The risk worth
           guarding is a subscription line silently losing its selling plan. */
        var before = null;
        try { before = await (await fetch('/cart.js')).json(); } catch (e2) {}

        var res2 = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          /* NO selling_plan. A suggestion is a one-time purchase unless the
             member chooses otherwise on the product page itself — quietly
             attaching a subscription to something someone tapped once is how
             people end up billed for things they did not agree to. */
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
        });
        if (!res2.ok) {
          var err = await res2.json().catch(function () { return {}; });
          msg('[data-cart-msg]', err.description || 'Could not add that right now.');
          return;
        }

        var after = null;
        try { after = await (await fetch('/cart.js')).json(); } catch (e3) {}
        var kept = true;
        if (before && after) {
          /* Every line that was there is still there, with its selling plan
             intact. */
          kept = (before.items || []).every(function (b) {
            return (after.items || []).some(function (a) {
              return a.key === b.key || (a.variant_id === b.variant_id &&
                String(a.selling_plan_allocation && a.selling_plan_allocation.selling_plan &&
                       a.selling_plan_allocation.selling_plan.id) ===
                String(b.selling_plan_allocation && b.selling_plan_allocation.selling_plan &&
                       b.selling_plan_allocation.selling_plan.id));
            });
          });
        }
        msg('[data-cart-msg]', kept
          ? 'Added as a one-time purchase. Your cart is otherwise unchanged \u2014 your workout is waiting.'
          : 'Added, but something else in your cart changed. Check your cart before you buy.');
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (err2) { msg('[data-cart-msg]', 'Could not reach the cart.'); }
      return;
    }
  }

  window.__bfScreensReady = true;
})();
